/**
 * Module dependencies.
 */

import { Button } from 'app/components/atoms/button/button';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CrudTemplate } from '../form-types';
import { FiltersForm } from './filters-form';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { useCanEditProject } from 'app/hooks/use-project-role';
import { useTranslation } from 'react-i18next';
import { ListChecks, X } from 'lucide-react';

/**
 * `Props` type.
 */

type Props = {
  template: CrudTemplate;
  appendFilters: (values: any) => void;
  refresh: () => Promise<any>;
  params: any;
  query: any;
  hasNested: boolean;
  // Provided (in drawer mode) to open the add drawer instead of navigating to
  // the add route.
  onAddDrawer?: () => void;
  // Bulk-edit toggle, rendered inline with the toolbar actions (next to refresh).
  bulkEdit?: any;
  bulkMode?: boolean;
  selectedCount?: number;
  onEnterBulk?: () => void;
  onExitBulk?: () => void;
};

/**
 * Export `CrudFilters` component.
 */

export function CrudFilters({
  template,
  hasNested,
  query,
  refresh,
  appendFilters,
  params,
  onAddDrawer,
  bulkEdit,
  bulkMode,
  selectedCount,
  onEnterBulk,
  onExitBulk
}: Props): JSX.Element {
  const [translate] = useTranslation();
  const canEditProject = useCanEditProject();
  const list = useMemo(() => template?.list, [template?.list]);
  const [searchParams] = useSearchParams();
  const filters = useMemo(() => list?.filters ?? [], [list?.filters]);
  const handleAppendFilters = useCallback(
    (data: any) => {
      appendFilters(template.list?.normalizeFilters?.(data));
    },
    [appendFilters, template.list]
  );

  useEffect(() => {
    if (hasNested) {
      appendFilters({});
    }
  }, [appendFilters, hasNested]);

  return (
    <div className={'mb-4'}>
      <FiltersForm
        appendFilters={handleAppendFilters}
        defaultQueryString={template?.list?.defaultQueryString}
        filters={filters}
        hasNested={hasNested}
      >
        <>
          {template?.list?.extraActions?.({ refresh })}

          {bulkEdit &&
            (bulkMode ? (
              <>
                <Button icon={<X className={'h-4 w-4'} />} onClick={onExitBulk} type={'default'}>
                  {bulkEdit.doneLabel ?? translate('common.actions.done')}
                </Button>

                <span className={'self-center text-sm text-muted-foreground'}>
                  {translate('common.bulk.selectedCount', { count: selectedCount ?? 0 })}
                </span>
              </>
            ) : (
              <Button icon={<ListChecks className={'h-4 w-4'} />} onClick={onEnterBulk}>
                {bulkEdit.label ?? translate('common.bulk.bulkEdit')}
              </Button>
            ))}

          {canEditProject &&
            template?.add &&
            (onAddDrawer ? (
              <Button onClick={onAddDrawer} type={'primary'}>
                {template.list?.addButton}
              </Button>
            ) : (
              !!template?.add?.route && (
                <Link
                  to={
                    template?.list?.interpolateAddLink?.(template, searchParams) ??
                    resolveAppPath(template?.add.route, params, query)
                  }
                >
                  <Button type={'primary'}>{template.list?.addButton}</Button>
                </Link>
              )
            ))}
        </>
      </FiltersForm>
    </div>
  );
}
